import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, MessageCircle, Repeat, Trash2 } from "lucide-react";
import { photoUrl } from "../../lib/photos";
import { BRAND, errMsg, today } from "./constants";

const KINDS = { cleaning: "🧹 Cleaning", repair: "🔧 Repair", laundry: "🧺 Laundry", other: "📌 Other" };
const NEXT = { todo: ["in_progress", "done"], in_progress: ["done"], done: ["todo"] };
const LABEL = { in_progress: "Start", done: "Mark done", todo: "Reopen" };
const COLOR = { todo: "#DC2626", in_progress: BRAND.orange, done: BRAND.green };

const waTask = (t, roomName) => {
  const n = String(t.assignee_phone || "").replace(/\D/g, "");
  const text = `Hi ${t.assignee_name || ""}, task for today (${t.due_date}):\n${KINDS[t.kind]} — ${t.title}${roomName ? ` · ${roomName}` : ""}${t.notes ? `\nNote: ${t.notes}` : ""}\nPlease reply DONE with a photo once finished.`;
  return `https://wa.me/${n.length === 10 ? `91${n}` : n}?text=${encodeURIComponent(text)}`;
};

function AddTask({ rooms, onAdd }) {
  const [f, setF] = useState({ title: "", kind: "cleaning", room_id: "", assignee_name: "", assignee_phone: "", due_date: today(), repeat_daily: false, notes: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const cls = "rounded-xl border-2 px-2.5 py-2 text-sm w-full";
  const submit = (e) => {
    e.preventDefault();
    onAdd({ ...f, room_id: f.room_id || null, assignee_name: f.assignee_name || null, assignee_phone: f.assignee_phone || null, notes: f.notes || null })
      .then((ok) => ok && setF({ ...f, title: "", notes: "" }));
  };
  return (
    <form onSubmit={submit} data-testid="task-add-form" className="grid grid-cols-2 md:grid-cols-4 gap-2">
      <input required placeholder="Task (Clean R101 bathroom)" data-testid="task-title-input" className={`${cls} col-span-2`} value={f.title} onChange={set("title")} />
      <select data-testid="task-kind-select" className={cls} value={f.kind} onChange={set("kind")}>{Object.entries(KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <select data-testid="task-room-select" className={cls} value={f.room_id} onChange={set("room_id")}>
        <option value="">Whole property</option>{rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      <input placeholder="Assign to (name)" data-testid="task-assignee-input" className={cls} value={f.assignee_name} onChange={set("assignee_name")} />
      <input placeholder="Staff WhatsApp" data-testid="task-assignee-phone" className={cls} value={f.assignee_phone} onChange={set("assignee_phone")} />
      <input type="date" required data-testid="task-due-input" className={cls} value={f.due_date} onChange={set("due_date")} />
      <label className="flex items-center gap-2 text-xs font-bold" style={{ color: BRAND.green }}>
        <input type="checkbox" data-testid="task-repeat-checkbox" checked={f.repeat_daily} onChange={set("repeat_daily")} /> Repeat daily
      </label>
      <input placeholder="Notes (optional)" data-testid="task-notes-input" className={`${cls} col-span-2 md:col-span-3`} value={f.notes} onChange={set("notes")} />
      <button data-testid="task-add-submit" className="rounded-full py-2 text-sm font-bold text-white" style={{ background: BRAND.green }}>Add task</button>
    </form>
  );
}

function TaskRow({ t, roomName, onMove, onPhoto, onDelete }) {
  return (
    <div data-testid={`task-row-${t.id}`} className="py-3 border-t flex flex-wrap items-center gap-3 text-sm" style={{ borderColor: BRAND.line }}>
      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${t.overdue ? "bed-blink" : ""}`} style={{ background: COLOR[t.status] }} />
      <div className="flex-1 min-w-[10rem]">
        <div className="font-bold">{KINDS[t.kind]} · {t.title} {t.repeat_daily && <Repeat className="inline w-3 h-3" style={{ color: BRAND.muted }} />}</div>
        <div className="text-xs" style={{ color: t.overdue ? "#DC2626" : BRAND.muted }} data-testid={`task-meta-${t.id}`}>
          {roomName} · {t.assignee_name || "unassigned"} · due {t.due_date}{t.overdue ? " · OVERDUE" : ""}{t.done_by ? ` · done by ${t.done_by}` : ""}
        </div>
      </div>
      {t.photo && <a href={photoUrl(t.photo)} target="_blank" rel="noreferrer"><img src={photoUrl(t.photo)} alt="proof" className="w-10 h-10 rounded-lg object-cover border" data-testid={`task-photo-${t.id}`} /></a>}
      <label data-testid={`task-photo-upload-${t.id}`} className="p-1.5 rounded-full cursor-pointer hover:bg-emerald-50" title="Photo proof">
        <Camera className="w-4 h-4" style={{ color: BRAND.green }} />
        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files[0] && onPhoto(t, e.target.files[0])} />
      </label>
      {t.assignee_phone && <a data-testid={`task-wa-${t.id}`} href={waTask(t, roomName)} target="_blank" rel="noreferrer" className="p-1.5 rounded-full hover:bg-green-50" title="Send on WhatsApp"><MessageCircle className="w-4 h-4" style={{ color: "#16A34A" }} /></a>}
      <span data-testid={`task-status-${t.id}`} className="text-[10px] font-bold uppercase" style={{ color: COLOR[t.status] }}>{t.status.replace("_", " ")}</span>
      {NEXT[t.status].map((s) => (
        <button key={s} data-testid={`task-${s}-${t.id}`} onClick={() => onMove(t, s)} className="rounded-full border px-2.5 py-1 text-[11px] font-bold" style={{ borderColor: COLOR[s], color: COLOR[s] }}>{LABEL[s]}</button>
      ))}
      <button data-testid={`task-delete-${t.id}`} onClick={() => onDelete(t)} aria-label="Delete task"><Trash2 className="w-4 h-4 text-red-600" /></button>
    </div>
  );
}

export function TasksPanel({ pid, authFetch, API }) {
  const [d, setD] = useState({ items: [], open: 0, overdue: 0 });
  const [rooms, setRooms] = useState([]);
  const [day, setDay] = useState(today());
  const load = useCallback(() => {
    authFetch({ url: `${API}/homestay/properties/${pid}/tasks?date=${day}` }).then((r) => setD(r.data)).catch(() => {});
    authFetch({ url: `${API}/homestay/properties/${pid}/board` }).then((r) => setRooms(r.data.rooms)).catch(() => {});
  }, [pid, authFetch, API, day]);
  useEffect(() => { load(); }, [load]);

  const call = async (cfg, msg) => {
    try { await authFetch(cfg); if (msg) toast.success(msg); load(); return true; }
    catch (e) { toast.error(errMsg(e, "Action failed")); return false; }
  };
  const add = (body) => call({ method: "post", url: `${API}/homestay/properties/${pid}/tasks`, data: body }, "Task added");
  const move = (t, status) => call({ method: "patch", url: `${API}/homestay/tasks/${t.id}`, data: { status } }, status === "done" ? "Task done ✓" : null);
  const del = (t) => {
    const stop = t.repeat_daily && window.confirm("Stop repeating this task every day too?");
    if (!t.repeat_daily && !window.confirm(`Delete "${t.title}"?`)) return;
    call({ method: "delete", url: `${API}/homestay/tasks/${t.id}${stop ? "?stop_repeat=true" : ""}` }, "Task deleted");
  };
  const photo = async (t, file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await authFetch({ method: "post", url: `${API}/upload/listing-photo`, data: form, headers: { "Content-Type": "multipart/form-data" } });
      call({ method: "patch", url: `${API}/homestay/tasks/${t.id}`, data: { photo: r.data.path } }, "Photo proof saved");
    } catch (e) { toast.error(errMsg(e, "Upload failed")); }
  };
  const roomName = (id) => rooms.find((r) => r.id === id)?.name || "Whole property";

  return (
    <div data-testid="tasks-panel" className="rounded-2xl border-2 bg-white p-4 space-y-3" style={{ borderColor: BRAND.line }}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-bold" style={{ color: BRAND.green }}>Housekeeping & repairs</div>
        <div className="flex items-center gap-3 text-xs font-bold">
          <span data-testid="tasks-open-count" style={{ color: BRAND.orange }}>{d.open} open</span>
          {d.overdue > 0 && <span data-testid="tasks-overdue-count" className="bed-blink" style={{ color: "#DC2626" }}>{d.overdue} overdue</span>}
          <input type="date" data-testid="tasks-date-filter" className="rounded-xl border-2 px-2 py-1" value={day} onChange={(e) => setDay(e.target.value)} />
        </div>
      </div>
      <AddTask rooms={rooms} onAdd={add} />
      <div data-testid="tasks-list">
        {d.items.length === 0 && <div className="text-xs" style={{ color: BRAND.muted }}>No tasks for this day.</div>}
        {d.items.map((t) => <TaskRow key={t.id} t={t} roomName={roomName(t.room_id)} onMove={move} onPhoto={photo} onDelete={del} />)}
      </div>
    </div>
  );
}
